import { formConvertErrorMessage } from "helpers/form_helper";
import { errorMessage } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useToasts } from "react-toast-notifications";
import timeSlotService from "services/timeSlotService";

export const useTimeSlots = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["common"]);
  const { enabled = true } = payload;
  return useQuery(
    ["time-slots", payload?.key ?? "all"],
    () => timeSlotService.getTimeSlots(payload),
    {
      enabled: enabled,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_time_slot", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: {} }),
      select: response => {
        const data = response?.items;
        return {
          items: data || [],
          params: response?._meta,
        };
      },
    }
  );
};
export const useTimeSlotsV2 = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["common"]);
  return useQuery(
    ["time-slots-v2", payload?.key ?? "all"],
    () => timeSlotService.getTimeSlotsV2(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_time_slot", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: {} }),
      select: response => {
        const data = response?.items;
        return {
          items: data || [],
          params: response?._meta,
        };
      },
    }
  );
};

// Create TimeSlot
export const useCreateTimeSlot = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["timeslot"]);
  const { addToast } = useToasts();

  return useMutation(timeSlotService.createTimeSlot, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.create", {
          target: t("timeslot"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.create", {
              target: t("timeslot"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Update TimeSlot
export const useUpdateTimeSlot = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["timeslot"]);
  const { addToast } = useToasts();

  return useMutation(timeSlotService.updateTimeSlot, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.update", {
          target: t("timeslot"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.update", {
              target: t("timeslot"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Delete TimeSlot
export const useDeleteTimeSlot = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["timeslot"]);
  const { addToast } = useToasts();

  return useMutation(timeSlotService.deleteTimeSlot, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.delete", {
          target: t("timeslot"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.delete", {
              target: t("timeslot"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Active TimeSlot
export const useActiveTimeSlot = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["timeslot"]);
  const { addToast } = useToasts();

  return useMutation(timeSlotService.activeTimeSlot, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.active", {
          target: t("timeslot"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.active", {
              target: t("timeslot"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useTimeSlotsSchedule = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["common"]);

  return useQuery(
    ["time-slots-schedule", payload?.key ?? "all"],
    () => timeSlotService.getTimeSlotsSchedule(payload),
    {
      enabled: true,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_time_slot", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: [],
      select: response => {
        return (
          response?.map(item => {
            return {
              value: item.id,
              label: item.name,
            };
          }) || []
        );
      },
    }
  );
};

export const useTimeSlotListType = (onSuccess, onError) => {
  const { t } = useTranslation(["timeslot"]);
  const { addToast } = useToasts();

  return useQuery(["timeslot-list-type"], () => timeSlotService.listType(), {
    enabled: true,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.timeslot-list-type", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    initialData: Object({ data: [] }),
    select: response => {
      const data = Object.keys(response).map(key => {
        return { value: key, label: response[key] };
      });

      if (typeof response === "object") {
        return data;
      }

      return response;
    },
  });
};
